import React, { useEffect, useMemo, useState } from "react";
import IsfReferenceDatePicker from "./IsfReferenceDatePicker";
import IsfReferenceDateHint from "./IsfReferenceDateHint";
import MilestoneRecipientStaff from "./MilestoneRecipientStaff";
import MilestoneRecipientRoles from "./MilestoneRecipientRoles";
import {
  buildAdHocStaffOptions,
  formatStudyPlaceholderDueOffset,
  isMilestoneLinkedStudyPlaceholder,
  removeStudyPlaceholder,
  saveAdHocStudyPlaceholders,
  updateStudyPlaceholder,
  studyPlaceholderMatchesFolder,
  studyPlaceholderMatchesSite,
  studyPlaceholderRequiresSignature,
  canRemoveStudyPlaceholderRow,
} from "./isfPlaceholderService";
import { defaultReferenceDateValue } from "./isfSiteSetupService";
import { fetchAssignableStaffLookup, fetchRoleLookup, resolveRecipientRoleIds } from "../../../services/lookupService";
import { resolveDefaultPiRoleId } from "./isfMilestoneService";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import "./TemplatePlaceholderManager.scss";

const emptyDraft = () => ({
  StudyPlaceholderId: "",
  MilestoneCode: "",
  PlaceholderName: "",
  Description: "",
  IsRequired: true,
  DueDate: defaultReferenceDateValue(),
  SelectedStaffIds: [],
  RequiresSignature: false,
  SignerRoles: "",
});

const validateDraft = (draft, { isEdit = false, roleOptions = [] } = {}) => {
  const errors = {};
  if (!(draft.MilestoneCode || "").trim()) {
    errors.MilestoneCode = "Code is required.";
  }
  if (!(draft.PlaceholderName || "").trim()) {
    errors.PlaceholderName = "Document placeholder name is required.";
  }
  if (!draft.DueDate) {
    errors.DueDate = "Due date is required.";
  }
  if (!isEdit && (!Array.isArray(draft.SelectedStaffIds) || draft.SelectedStaffIds.length === 0)) {
    errors.SelectedStaffIds = "Select at least one staff member.";
  }
  if (draft.RequiresSignature) {
    const signerRoleIds = resolveRecipientRoleIds(draft.SignerRoles, roleOptions);
    if (!signerRoleIds.length) {
      errors.SignerRoles = "Select at least one signer role.";
    }
  }
  return errors;
};

const requiredLabel = (row) =>
  row?.IsRequired === false || row?.IsRequired === 0 ? "Optional" : "Required";

const formatDueDisplay = (dueDate) => {
  if (!dueDate) return null;
  const d = new Date(dueDate);
  if (Number.isNaN(d.getTime())) return String(dueDate);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const isAdHocPlaceholder = (row) =>
  row?.IsAdHoc === true ||
  row?.IsAdHoc === 1 ||
  row?.IsAdHoc === "1" ||
  row?.isAdHoc === true ||
  row?.isAdHoc === 1;

const normalizeStaffName = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const stripAdHocStaffSuffix = (placeholderName, assignedToName) => {
  const name = String(placeholderName || "").trim();
  const staff = String(assignedToName || "").trim();
  if (!name || !staff) return name;

  const suffix = ` - ${staff}`;
  if (name.endsWith(suffix)) return name.slice(0, -suffix.length).trim();

  if (name.toLowerCase().endsWith(suffix.toLowerCase())) {
    return name.slice(0, name.length - suffix.length).trim();
  }

  return name;
};

const getAdHocBaseName = (row) => {
  const placeholderName = String(row?.PlaceholderName || "").trim();
  const milestoneName = String(row?.MilestoneName || "").trim();
  const staff = String(row?.AssignedToName || "").trim();

  if (milestoneName && normalizeStaffName(milestoneName) !== normalizeStaffName(placeholderName)) {
    if (!staff || !milestoneName.toLowerCase().includes(staff.toLowerCase())) {
      return milestoneName;
    }
  }

  if (staff) {
    const stripped = stripAdHocStaffSuffix(placeholderName, staff);
    if (stripped) return stripped;
  }

  if (row?.AssignedToStaffId && placeholderName.includes(" - ")) {
    return placeholderName.split(" - ")[0].trim();
  }

  return milestoneName || placeholderName;
};

const getPlaceholderCode = (row) => String(row?.MilestoneCode || "").trim();

const getPlaceholderDisplayName = (row) => {
  if (!row) return "";
  if (isAdHocPlaceholder(row)) return getAdHocBaseName(row);
  return String(row?.PlaceholderName || row?.MilestoneName || "").trim();
};

const getPlaceholderAnchorFormula = (row) => {
  const directRule = String(row?.DueAnchorRule ?? row?.dueAnchorRule ?? "").trim();
  if (directRule) return directRule;
  return formatStudyPlaceholderDueOffset(row) || null;
};

const getAssigneeLabel = (row) => {
  if (!isAdHocPlaceholder(row)) return null;
  const staffName = String(row?.AssignedToName || "").trim();
  if (staffName) return { label: staffName, type: "staff" };
  return null;
};

const getAssigneeRoleIds = (row, roleOptions = []) => {
  if (isAdHocPlaceholder(row)) return [];
  const raw = String(
    row?.DefaultAssigneeRole ?? row?.defaultAssigneeRole ?? "",
  ).trim();
  if (!raw) return [];
  return resolveRecipientRoleIds(raw, roleOptions);
};

const getSignerRoleIds = (row, roleOptions = []) => {
  if (!studyPlaceholderRequiresSignature(row)) return [];
  return resolveRecipientRoleIds(row?.SignerRoles ?? row?.signerRoles, roleOptions);
};

const formatDueLine = (row) => {
  if (row?.DueDate) return `Due ${formatDueDisplay(row.DueDate)}`;
  if (
    row?.DueDateIsProvisional ||
    row?.DueDateIsProvisional === 1 ||
    isMilestoneLinkedStudyPlaceholder(row)
  ) {
    return "Due date pending";
  }
  return null;
};

const statusToneClass = (status) => {
  const value = String(status || "Pending").toLowerCase();
  if (value === "overdue") return "is-overdue";
  if (value === "fulfilled") return "is-fulfilled";
  if (value === "waived") return "is-waived";
  return "is-pending";
};

const StudyPlaceholderListCard = ({
  row,
  isSelected,
  canEditAdHoc,
  canRemoveRow,
  saving,
  roleOptions = [],
  onEdit,
  onRemove,
}) => {
  const code = getPlaceholderCode(row);
  const name = getPlaceholderDisplayName(row);
  const description = String(row?.Description || "").trim();
  const isRequired = row?.IsRequired !== false && row?.IsRequired !== 0;
  const assignee = getAssigneeLabel(row);
  const assigneeRoleIds = isAdHocPlaceholder(row)
    ? []
    : getAssigneeRoleIds(row, roleOptions);
  const signerRoleIds = getSignerRoleIds(row, roleOptions);
  const requiresSignature = studyPlaceholderRequiresSignature(row);
  const anchorFormula = getPlaceholderAnchorFormula(row);
  const dueLine = formatDueLine(row);
  const showMetaRow =
    dueLine ||
    assignee ||
    assigneeRoleIds.length > 0 ||
    signerRoleIds.length > 0 ||
    requiresSignature ||
    anchorFormula;

  return (
    <div
      className={`tpl-ph-manager__card study-ph-card${
        isSelected ? " is-selected" : ""
      }${canModifyRow(row) ? "" : " is-readonly"}`}
    >
      <div className="study-ph-card__row study-ph-card__row--title">
        <div
          className="study-ph-card__title"
          title={[code, name].filter(Boolean).join(" · ")}
        >
          {code ? <strong className="study-ph-card__code">{code}</strong> : null}
          {code && name ? <span className="study-ph-card__sep">·</span> : null}
          {name ? <span className="study-ph-card__name">{name}</span> : null}
          {requiresSignature ? (
            <i
              className="fas fa-file-signature study-ph-card__sig-icon"
              title="Signature required"
              aria-label="Signature required"
            />
          ) : null}
          {code || name ? (
            <span
              className={`study-ph-card__required${
                isRequired ? " is-required" : " is-optional"
              }`}
            >
              {requiredLabel(row)}
            </span>
          ) : null}
        </div>
        {row?.Status ? (
          <span className={`study-ph-card__status ${statusToneClass(row.Status)}`}>
            {row.Status}
          </span>
        ) : null}
      </div>

      {description ? (
        <div className="study-ph-card__row study-ph-card__row--desc" title={description}>
          {description}
        </div>
      ) : null}

      {showMetaRow ? (
        <div className="study-ph-card__row study-ph-card__row--meta">
          {dueLine ? <span className="study-ph-card__due">{dueLine}</span> : null}
          {assignee ? (
            <span
              className="study-ph-card__avatar"
              style={{ backgroundColor: getAvatarColor(assignee.label) }}
              title={`Assigned to ${assignee.label}`}
            >
              {getInitials(assignee.label)}
            </span>
          ) : null}
          {assigneeRoleIds.length > 0 ? (
            <div className="study-ph-card__roles">
              <MilestoneRecipientRoles
                selectedRoleIds={assigneeRoleIds}
                allRoles={roleOptions}
                disabled
              />
            </div>
          ) : null}
          {requiresSignature ? (
            <span className="study-ph-card__sig-badge">Signature required</span>
          ) : null}
          {signerRoleIds.length > 0 ? (
            <div className="study-ph-card__roles study-ph-card__roles--signers">
              <MilestoneRecipientRoles
                selectedRoleIds={signerRoleIds}
                allRoles={roleOptions}
                disabled
              />
            </div>
          ) : null}
          {anchorFormula ? (
            <span className="study-ph-card__anchor">{anchorFormula}</span>
          ) : null}
        </div>
      ) : null}

      {canModifyRow(row) && (canEditAdHoc || canRemoveRow) ? (
        <div className="study-ph-card__row study-ph-card__row--actions">
          {row.IsAdHoc && canEditAdHoc ? (
            <button
              type="button"
              className="btn btn-sm btn-link p-0"
              disabled={saving}
              onClick={() => onEdit(row)}
            >
              Edit
            </button>
          ) : null}
          {canRemoveRow ? (
            <button
              type="button"
              className="btn btn-sm btn-link p-0 text-danger"
              disabled={saving}
              onClick={() => onRemove(row)}
            >
              Remove
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const canModifyRow = (row) =>
  row && !row.DocumentIdRef && row.Status !== "Waived" && row.Status !== "Fulfilled";

const StudyPlaceholderManager = ({
  studyId,
  organizationId,
  createdBy,
  templateCategory,
  siteIdRef,
  selectedFolderId,
  selectedFolderName,
  placeholders = [],
  staffList = [],
  userData,
  permissions = [],
  canCreateAdHoc = false,
  canEditAdHoc = false,
  readOnly = false,
  onRefresh,
  showToast,
  showAlert,
}) => {
  const [draft, setDraft] = useState(emptyDraft());
  const [editId, setEditId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [orgStaffRows, setOrgStaffRows] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  useEffect(() => {
    if (!userData?.OrganizationId) return;
    fetchAssignableStaffLookup(userData)
      .then((rows) => setOrgStaffRows(rows || []))
      .catch(() => setOrgStaffRows([]));
    fetchRoleLookup(userData)
      .then((roles) =>
        setRoleOptions(
          (roles || []).map((role) => ({
            roleId: role.roleId,
            roleName: role.roleName,
          })),
        ),
      )
      .catch(() => setRoleOptions([]));
  }, [userData]);

  const eligibleStaff = useMemo(
    () => buildAdHocStaffOptions(staffList, orgStaffRows, siteIdRef),
    [staffList, orgStaffRows, siteIdRef],
  );

  const folderRows = useMemo(
    () =>
      (placeholders || []).filter(
        (p) =>
          studyPlaceholderMatchesFolder(p, selectedFolderId) &&
          studyPlaceholderMatchesSite(p, siteIdRef),
      ),
    [placeholders, selectedFolderId, siteIdRef],
  );

  const createCount = draft.SelectedStaffIds?.length || 0;
  const allowCreate = canCreateAdHoc && !readOnly;
  const allowEdit = canEditAdHoc && !readOnly;

  const resetForm = () => {
    setEditId(null);
    setFieldErrors({});
    setDraft(emptyDraft());
  };

  useEffect(() => {
    resetForm();
  }, [selectedFolderId]);

  const openEditForm = (row) => {
    if (readOnly || !canModifyRow(row) || !row.IsAdHoc || !canEditAdHoc) return;
    setEditId(row.StudyPlaceholderId);
    setFieldErrors({});
    setDraft({
      StudyPlaceholderId: row.StudyPlaceholderId,
      MilestoneCode: row.MilestoneCode || "",
      PlaceholderName: getAdHocBaseName(row),
      Description: row.Description || "",
      IsRequired: row.IsRequired !== false && row.IsRequired !== 0,
      DueDate:
        row.DueDate && !Number.isNaN(new Date(row.DueDate).getTime())
          ? defaultReferenceDateValue(new Date(row.DueDate))
          : defaultReferenceDateValue(),
      SelectedStaffIds: [],
      RequiresSignature: studyPlaceholderRequiresSignature(row),
      SignerRoles: row.SignerRoles || row.signerRoles || "",
    });
  };

  const setSignerRoles = (roleIds) => {
    setDraft((prev) => ({
      ...prev,
      SignerRoles: [...new Set((roleIds || []).filter(Boolean))].join(","),
    }));
    if (fieldErrors.SignerRoles) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.SignerRoles;
        return next;
      });
    }
  };

  const handleSave = async () => {
    if (readOnly) {
      showAlert?.({
        title: "Read-only preview",
        message:
          "Switch to the current study phase before adding or editing placeholders.",
        type: "info",
      });
      return;
    }
    if (editId) {
      if (!canEditAdHoc) {
        showAlert?.({
          title: "Not allowed",
          message: "You do not have permission to edit ad-hoc placeholders.",
          type: "warning",
        });
        return;
      }
    } else if (!canCreateAdHoc) {
      showAlert?.({
        title: "Not allowed",
        message: "You do not have permission to create ad-hoc placeholders.",
        type: "warning",
      });
      return;
    }
    const errors = validateDraft(draft, { isEdit: Boolean(editId), roleOptions });
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    try {
      setSaving(true);
      const milestoneCode = draft.MilestoneCode.trim().toUpperCase();

      if (editId) {
        await updateStudyPlaceholder({
          organizationId,
          studyId,
          studyPlaceholderId: editId,
          placeholderName: draft.PlaceholderName.trim(),
          description: (draft.Description || "").trim(),
          isRequired: true,
          dueDate: draft.DueDate,
          milestoneCode,
          siteIdRef,
          requiresSignature: draft.RequiresSignature,
          signerRoles: draft.SignerRoles,
          createdBy,
        });
        showToast?.({ message: "Ad-hoc placeholder updated.", type: "success" });
      } else {
        const response = await saveAdHocStudyPlaceholders({
          organizationId,
          studyId,
          folderIdRef: selectedFolderId,
          templateCategory,
          siteIdRef,
          milestoneCode,
          placeholderName: draft.PlaceholderName.trim(),
          description: (draft.Description || "").trim(),
          isRequired: true,
          dueDate: draft.DueDate,
          staffIds: draft.SelectedStaffIds || [],
          requiresSignature: draft.RequiresSignature,
          signerRoles: draft.SignerRoles,
          createdBy,
        });

        const created =
          response?.SuccessStory?.[0]?.CreatedCount ??
          response?.Tables?.[0]?.Rows?.[0]?.CreatedCount ??
          createCount;

        showToast?.({
          message: `${created} ad-hoc placeholder${Number(created) === 1 ? "" : "s"} created.`,
          type: "success",
        });
      }

      resetForm();
      await onRefresh?.();
    } catch (err) {
      showAlert?.({
        title: "Error",
        message: err?.message || "Failed to save ad-hoc placeholder.",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (row) => {
    if (readOnly) {
      showAlert?.({
        title: "Read-only preview",
        message:
          "Switch to the current study phase before removing placeholders.",
        type: "info",
      });
      return;
    }
    if (!canModifyRow(row)) return;
    if (!canRemoveStudyPlaceholderRow(row, permissions, userData)) {
      showAlert?.({
        title: "Not allowed",
        message: row.IsAdHoc
          ? "You do not have permission to remove ad-hoc placeholders."
          : "You do not have permission to remove study placeholders.",
        type: "warning",
      });
      return;
    }

    try {
      setSaving(true);
      await removeStudyPlaceholder({
        organizationId,
        studyId,
        studyPlaceholderId: row.StudyPlaceholderId,
        createdBy,
      });
      if (editId === row.StudyPlaceholderId) resetForm();
      showToast?.({ message: "Document placeholder removed.", type: "success" });
      await onRefresh?.();
    } catch (err) {
      showAlert?.({
        title: "Error",
        message: err?.message || "Failed to remove document placeholder.",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!selectedFolderId) {
    return (
      <div className="tpl-ph-manager tpl-ph-manager--empty">
        <div className="tpl-ph-manager__empty-state">
          <i className="fa-regular fa-folder-open" />
          <p>Select a folder to manage document placeholders for this study.</p>
        </div>
      </div>
    );
  }

  const staffEmptyMessage = siteIdRef
    ? "No staff assigned to this study and site."
    : "No staff assigned to this study.";

  return (
    <div className="tpl-ph-manager study-ph-manager">
      <div className="tpl-ph-manager__header">
        <h6 className="tpl-ph-manager__folder-name">{selectedFolderName || "Folder"}</h6>
        <p className="study-ph-manager__header-hint mb-0">
          <i className="fa fa-info-circle" aria-hidden="true" />
          <span>
            {readOnly
              ? "Viewing another study phase. Switch to the current phase to add or edit placeholders."
              : "Add ad-hoc placeholders with a milestone code for reporting. One placeholder is created per selected staff member."}
          </span>
        </p>
      </div>

      <div
        className={`tpl-ph-manager__sections${allowCreate ? "" : " tpl-ph-manager__sections--list-only"}`}
      >
        {allowCreate && (
          <div className="tpl-ph-manager__section tpl-ph-manager__section--add">
            <div className="tpl-ph-manager__section-head">
              <h6>{editId ? "Edit ad-hoc placeholder" : "Add ad-hoc placeholder"}</h6>
              {editId && (
                <button type="button" className="btn btn-sm btn-link p-0" onClick={resetForm}>
                  New
                </button>
              )}
            </div>

            <div className="tpl-ph-manager__section-body">
              <div className="row g-2">
                <div className="col-md-4">
                  <label className="tpl-ph-manager__field-label">
                    Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control form-control-sm${
                      fieldErrors.MilestoneCode ? " is-invalid" : ""
                    }`}
                    value={draft.MilestoneCode}
                    disabled={saving}
                    placeholder="DOC1"
                    onChange={(e) => {
                      setDraft((p) => ({
                        ...p,
                        MilestoneCode: e.target.value.toUpperCase(),
                      }));
                      if (fieldErrors.MilestoneCode) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.MilestoneCode;
                          return next;
                        });
                      }
                    }}
                  />
                  {fieldErrors.MilestoneCode && (
                    <div className="tpl-ph-manager__field-error">{fieldErrors.MilestoneCode}</div>
                  )}
                </div>

                <div className="col-md-8">
                  <label className="tpl-ph-manager__field-label">
                    Document placeholder name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control form-control-sm${
                      fieldErrors.PlaceholderName ? " is-invalid" : ""
                    }`}
                    value={draft.PlaceholderName}
                    disabled={saving}
                    onChange={(e) => {
                      setDraft((p) => ({ ...p, PlaceholderName: e.target.value }));
                      if (fieldErrors.PlaceholderName) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.PlaceholderName;
                          return next;
                        });
                      }
                    }}
                  />
                  {fieldErrors.PlaceholderName && (
                    <div className="tpl-ph-manager__field-error">{fieldErrors.PlaceholderName}</div>
                  )}
                </div>

                <div className="col-12">
                  <label className="tpl-ph-manager__field-label">Description</label>
                  <textarea
                    className="form-control form-control-sm study-ph-manager__description"
                    rows={2}
                    value={draft.Description}
                    disabled={saving}
                    onChange={(e) => setDraft((p) => ({ ...p, Description: e.target.value }))}
                  />
                </div>

                <div className="col-12">
                  <label className="tpl-ph-manager__field-label">
                    Due date <span className="text-danger">*</span>
                  </label>
                  <IsfReferenceDatePicker
                    value={draft.DueDate}
                    onChange={(value) => setDraft((p) => ({ ...p, DueDate: value }))}
                    disabled={saving}
                  />
                  {fieldErrors.DueDate && (
                    <div className="tpl-ph-manager__field-error">{fieldErrors.DueDate}</div>
                  )}
                </div>

                {!editId ? (
                  <div className="col-12">
                    <label className="tpl-ph-manager__field-label">
                      Staff <span className="text-danger">*</span>
                    </label>
                    <MilestoneRecipientStaff
                      selectedStaffIds={draft.SelectedStaffIds}
                      allStaff={eligibleStaff}
                      disabled={saving}
                      emptyStaffMessage={staffEmptyMessage}
                      onChange={(staffIds) => {
                        setDraft((p) => ({ ...p, SelectedStaffIds: staffIds }));
                        if (fieldErrors.SelectedStaffIds) {
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.SelectedStaffIds;
                            return next;
                          });
                        }
                      }}
                    />
                    {fieldErrors.SelectedStaffIds && (
                      <div className="tpl-ph-manager__field-error">
                        {fieldErrors.SelectedStaffIds}
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="col-12">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="adhoc-requires-signature"
                      checked={!!draft.RequiresSignature}
                      disabled={saving}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setDraft((p) => ({
                          ...p,
                          RequiresSignature: checked,
                          SignerRoles: checked
                            ? p.SignerRoles ||
                              resolveDefaultPiRoleId(roleOptions) ||
                              ""
                            : "",
                        }));
                      }}
                    />
                    <label className="form-check-label" htmlFor="adhoc-requires-signature">
                      Requires signature
                    </label>
                  </div>
                </div>

                {draft.RequiresSignature ? (
                  <div className="col-12">
                    <label className="tpl-ph-manager__field-label">
                      Signer role(s) <span className="text-danger">*</span>
                    </label>
                    <MilestoneRecipientRoles
                      selectedRoleIds={resolveRecipientRoleIds(
                        draft.SignerRoles,
                        roleOptions,
                      )}
                      allRoles={roleOptions}
                      disabled={saving}
                      showSearch
                      excludeSelectedFromPicker
                      onChange={setSignerRoles}
                    />
                    {fieldErrors.SignerRoles && (
                      <div className="tpl-ph-manager__field-error">{fieldErrors.SignerRoles}</div>
                    )}
                  </div>
                ) : null}

                {!editId && createCount > 0 ? (
                  <div className="col-12">
                    <IsfReferenceDateHint
                      text={`Will create ${createCount} placeholder${createCount === 1 ? "" : "s"} for this folder.`}
                    />
                  </div>
                ) : null}

                <div className="col-12">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm w-100"
                    disabled={saving}
                    onClick={handleSave}
                  >
                    {saving
                      ? "Saving..."
                      : editId
                        ? "Update placeholder"
                        : "Create placeholder(s)"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="tpl-ph-manager__section tpl-ph-manager__section--list">
          <div className="tpl-ph-manager__section-head">
            <h6>Document placeholders</h6>
            <span className="tpl-ph-manager__count">
              {folderRows.length} item{folderRows.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="tpl-ph-manager__section-body tpl-ph-manager__section-body--scroll">
            {folderRows.length === 0 ? (
              <div className="tpl-ph-manager__list-empty">
                <p>No document placeholders on this folder yet.</p>
              </div>
            ) : (
              <div className="tpl-ph-manager__list">
                {folderRows.map((row) => (
                  <StudyPlaceholderListCard
                    key={row.StudyPlaceholderId}
                    row={row}
                    isSelected={editId === row.StudyPlaceholderId}
                    canEditAdHoc={allowEdit}
                    canRemoveRow={
                      !readOnly &&
                      canRemoveStudyPlaceholderRow(row, permissions, userData)
                    }
                    saving={saving}
                    roleOptions={roleOptions}
                    onEdit={openEditForm}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyPlaceholderManager;
